import React from 'react';
import { ChevronRight } from 'lucide-react';
import { ApprovalPopup, DisputePopup, hasDispute } from '@app/components/core';
import cx from 'classnames';
import { clientReportingPath, productReportingPath, productTabPath } from '@app/constants/url';
import { capitalize, formatDateString } from '@app/utils';
import {
  SprintProductClientEntity,
  ClientProductEntity,
  ClientReportProductEntity,
  useApproveSprintProduct,
  useDisputeSprintProduct,
  useListSprintProducts,
  ApproveSprintFormValues,
  DisputeSprintFormValues,
} from '@data';
import { Language } from '@app/constants/enums';
import { cardBleedClass } from '@app/constants/styles';
import { useTranslation } from 'react-i18next';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useIsInternalReader } from '../internalReader';

type ClientReportEntity = SprintProductClientEntity & { products: ClientReportProductEntity[] };

const rowClass =
  'flex cursor-pointer items-center gap-3 rounded-lg p-6 outline-none transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring max-md:p-4';

const Row = ({ children, handleAction }: { children: React.ReactNode; handleAction: () => void }) => (
  <div
    className={rowClass}
    role="button"
    tabIndex={0}
    onClick={handleAction}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleAction();
      }
    }}
  >
    <div className="flex min-w-0 flex-1 flex-wrap justify-between max-md:flex-col">{children}</div>
    {/* Says the row opens something; nothing else on it does. */}
    <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
  </div>
);

const ClientReportCard = ({
  report,
  showNewBadge,
  isApprovable,
  organizationId,
  productName,
}: {
  report: ClientReportEntity;
  // "New" badge = the client hasn't opened this sprint report yet (visited-state, not approval).
  showNewBadge?: boolean;
  // Whether the report can still be approved (i.e. it isn't approved yet).
  isApprovable?: boolean;
  organizationId?: string;
  /*
   * Which product the report is for, when the list is not already about one. The product's own Sprints
   * tab leaves this out — the page is the product, so naming it on every row says nothing. A list that
   * crosses products has to lead with it, or the rows are indistinguishable.
   */
  productName?: string;
}): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { sprint, products } = report;
  // One product at a time, and which of the two answers is being given to it. Both dialogs read the same
  // report, so they share the query that fetches it.
  const [deciding, setDeciding] = React.useState<null | { product: ClientProductEntity; kind: 'approve' | 'dispute' }>(
    null,
  );
  const sprintProductInfo = deciding?.product;
  const { mutate: approveSprintProduct, isPending: isApproving } = useApproveSprintProduct();
  const { mutate: disputeSprintProduct, isPending: isDisputing } = useDisputeSprintProduct();
  const resetApproveInfo = () => setDeciding(null);

  const handleApproveSprintProduct = (values: ApproveSprintFormValues) => {
    approveSprintProduct(values, {
      onSuccess: () => {
        resetApproveInfo();
      },
    });
  };

  const handleDisputeSprintProduct = (values: DisputeSprintFormValues) => {
    disputeSprintProduct(values, {
      onSuccess: () => {
        resetApproveInfo();
      },
    });
  };

  const { data: sprintProduct, isLoading } = useListSprintProducts({
    sprintId: sprint.id,
    productId: sprintProductInfo?.id,
  });

  const transitionNavigate = useViewTransitionNav();
  // The preview never offers the two answers: approving on the client's behalf is worse than read-only.
  const internal = useIsInternalReader();
  const canDecide = isApprovable && !internal;

  const decideAction = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>,
    product: ClientProductEntity,
    kind: 'approve' | 'dispute',
  ) => {
    e.stopPropagation();
    setDeciding({ product, kind });
  };

  return (
    <>
      <div className={cx('my-3 flex flex-col rounded-lg border border-border bg-card shadow-sm', cardBleedClass)}>
        {products.map((p, index) => {
          // Their own remark is what a disputed report is waiting on, so there is nothing for them to
          // approve until we answer it. It says which of the two it is rather than going blank.
          const isDisputed = hasDispute(p.report);
          const productAction = isApprovable && isDisputed && (
            <span className="p-1 text-sm font-bold text-muted-foreground">{t('actions.disputed')}</span>
          );

          const productItem = (
            <div
              className={cx(
                'flex w-[45%] max-w-full items-center justify-end gap-2 text-end max-md:w-full',
                index > 0 && 'max-md:mt-9',
              )}
            >
              {canDecide && !isDisputed ? (
                // Both answers here too: a client who disagrees should not have to open the report to say so.
                <React.Fragment key={`decide-${p.id}`}>
                  <div
                    className="cursor-pointer rounded p-1 text-sm font-bold text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        decideAction(e, p, 'dispute');
                      }
                    }}
                    onClick={e => decideAction(e, p, 'dispute')}
                    role="button"
                    tabIndex={0}
                  >
                    {t('actions.dispute')}
                  </div>
                  <div
                    className="cursor-pointer rounded p-1 text-sm font-bold text-primary focus-visible:ring-2 focus-visible:ring-ring"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        decideAction(e, p, 'approve');
                      }
                    }}
                    onClick={e => decideAction(e, p, 'approve')}
                    role="button"
                    tabIndex={0}
                  >
                    {t('approve')}
                  </div>
                </React.Fragment>
              ) : (
                productAction || <div key={`empty-${p.id}`} />
              )}
            </div>
          );

          const handleAction = () => {
            // One of us opens the sprint where it is written and closed; the client reads their report.
            if (internal) {
              transitionNavigate(productReportingPath(organizationId ?? '', p.id, sprint.id), 'forward', {
                state: {
                  backPath: productTabPath(organizationId ?? '', p.id, 'sprints'),
                  backLabel: t('actions.backToName', { name: capitalize(p.name) ?? p.name }),
                },
              });
              return;
            }
            transitionNavigate(clientReportingPath(organizationId, p.id, sprint.id), 'forward');
          };

          const sprintItem = (
            <div className="flex w-[45%] max-w-full flex-col gap-2 text-start max-md:w-full">
              <div className="flex flex-wrap items-center gap-2">
                {/* The product leads when there is one to name, and the sprint drops to the line below
                    beside the dates — one row's worth of height either way. */}
                <span className="font-bold text-foreground">{productName ?? `${t('sprint')} ${sprint.number}`}</span>
                {showNewBadge && (
                  <span className="rounded bg-zinc-200 px-2 py-0.5 text-xs font-bold capitalize text-primary dark:bg-zinc-700">
                    {t('tabs.new')}
                  </span>
                )}
              </div>

              <div className="text-sm text-muted-foreground">
                {productName && `${t('sprint')} ${sprint.number} · `}
                {`${formatDateString(sprint.startDate, language as Language, 'dd MMM')} – ${formatDateString(
                  sprint.endDate,
                  language as Language,
                  'dd MMM yyyy',
                )}`}
              </div>
            </div>
          );

          if (index === 0) {
            return (
              <Row key={`${p.id}-${sprint.id}`} handleAction={handleAction}>
                {sprintItem}
                {productItem}
              </Row>
            );
          }

          return (
            <Row key={`${p.id}-${sprint.id}`} handleAction={handleAction}>
              <div className="h-0 w-[45%] min-w-0 overflow-hidden max-md:w-0" aria-hidden />
              {productItem}
            </Row>
          );
        })}
      </div>
      {deciding?.kind === 'approve' && (
        <ApprovalPopup
          approveSprintProduct={handleApproveSprintProduct}
          sprintProduct={{ ...sprintProduct?.items[0] }}
          productId={deciding.product.id}
          sprintId={sprint.id}
          onClose={resetApproveInfo}
          isLoading={isLoading}
          isApproving={isApproving}
        />
      )}
      {deciding?.kind === 'dispute' && (
        <DisputePopup
          disputeSprintProduct={handleDisputeSprintProduct}
          sprintProduct={{ ...sprintProduct?.items[0] }}
          productId={deciding.product.id}
          sprintId={sprint.id}
          onClose={resetApproveInfo}
          isLoading={isLoading}
          isDisputing={isDisputing}
        />
      )}
    </>
  );
};

export default ClientReportCard;
